import axios from 'axios';
import ENVIRONMENTS, {
  getKeycloakRedirectUri,
  getKeycloakSilentAuthUrl,
  isConfiguredKeycloakUrl,
} from '../../environments';
import { renewAuthTokens } from '../../store/common/common.reducer';
import { store } from '../../store/create-store';

interface TokenClaims {
  exp?: number;
  iat?: number;
  sub?: string;
}

interface RenewedTokens {
  token: string;
  idToken?: string;
}

const MAX_REFRESH_MARGIN_MS = 60 * 1000;
const EXPIRY_GRACE_MS = 10 * 1000;
const FRAME_TIMEOUT_MS = 10 * 1000;
const EXCHANGE_TIMEOUT_MS = 15 * 1000;
const REFUSED_COOLDOWN_MS = 30 * 1000;
const TRANSIENT_COOLDOWN_MS = 3 * 1000;
const DIALOG_POLL_MS = 1000;
const TOP_LEVEL_RETRY_MS = 60 * 1000;
const TOP_LEVEL_AT_KEY = 'qc_top_level_renewal_at';
const RENEWAL_KEY = 'qc_top_level_renewal';
export const POST_AUTH_RETURN_KEY = 'qc_post_auth_return';

let clockSkewMs = 0;
let renewal: Promise<string> | null = null;
let retryAfter = 0;
let dialogWait: number | undefined;
let framesRefused = false;

const refused = (message: string) =>
  Object.assign(new Error(message), { refused: true });

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

export function readTokenClaims(token?: string | null): TokenClaims | null {
  const payload = token?.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const claims = JSON.parse(atob(base64 + padding));
    return claims && typeof claims === 'object' ? claims : null;
  } catch {
    return null;
  }
}

function msUntilExpiry(claims: TokenClaims, now: number): number | null {
  if (!claims.exp) return null;
  return claims.exp * 1000 + clockSkewMs - now;
}

export function tokenNeedsRenewal(
  token?: string | null,
  now = Date.now(),
): boolean {
  const claims = readTokenClaims(token);
  const remaining = claims ? msUntilExpiry(claims, now) : null;
  if (!claims || remaining === null) return false;
  const lifetimeMs =
    claims.iat && claims.exp && claims.exp > claims.iat
      ? (claims.exp - claims.iat) * 1000
      : MAX_REFRESH_MARGIN_MS * 3;
  return remaining <= Math.min(MAX_REFRESH_MARGIN_MS, lifetimeMs / 3);
}

export function tokenIsExpiring(
  token?: string | null,
  now = Date.now(),
): boolean {
  const claims = readTokenClaims(token);
  const remaining = claims ? msUntilExpiry(claims, now) : null;
  return remaining !== null && remaining <= EXPIRY_GRACE_MS;
}

export function tokenHasExpired(
  token?: string | null,
  now = Date.now(),
): boolean {
  const claims = readTokenClaims(token);
  const remaining = claims ? msUntilExpiry(claims, now) : null;
  return remaining !== null && remaining <= 0;
}

export function learnClockSkew(
  token?: string | null,
  receivedAt = Date.now(),
): void {
  const iat = readTokenClaims(token)?.iat;
  if (iat) clockSkewMs = receivedAt - iat * 1000;
}

/** Safari, and every iOS browser, keep the Keycloak cookie out of a cross-site frame. */
export function blocksThirdPartyCookies(
  nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'> = window.navigator,
): boolean {
  const ua = nav.userAgent || '';
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  if (/Macintosh/.test(ua) && (nav.maxTouchPoints || 0) > 1) return true;
  return (
    /AppleWebKit/.test(ua) &&
    /Version\/[\d.]+.*Safari/.test(ua) &&
    !/Chrome|Chromium|CriOS|Edg|OPR|Firefox|FxiOS|Android/.test(ua)
  );
}

export function inPageRenewalWorks(): boolean {
  return !framesRefused && !blocksThirdPartyCookies();
}

export function isOnAuthCallback(): boolean {
  return (
    Boolean(
      new URLSearchParams(window.location.hash.replace(/^#/, '')).get('code'),
    ) || window.location.pathname === '/redirect'
  );
}

export function topLevelRenewalIsRecent(
  now = Date.now(),
  windowMs = TOP_LEVEL_RETRY_MS,
): boolean {
  try {
    const at = Number(sessionStorage.getItem(TOP_LEVEL_AT_KEY));
    return Boolean(at) && now - at < windowMs;
  } catch {
    return false;
  }
}

export interface TopLevelRenewal {
  state: string;
  redirectUri: string;
  appRedirectUri?: string | null;
}

/** Returns the state the Keycloak trip carries, so only its own callback redeems with this redirect_uri. */
export function markTopLevelRenewal(
  redirectUri: string,
  now = Date.now(),
): string {
  const state =
    typeof window.crypto?.getRandomValues === 'function'
      ? randomState()
      : `${now.toString(36)}${Math.random().toString(36).slice(2)}`;
  try {
    const renewal: TopLevelRenewal = {
      state,
      redirectUri,
      appRedirectUri: localStorage.getItem('redirect_uri'),
    };
    sessionStorage.setItem(TOP_LEVEL_AT_KEY, String(now));
    sessionStorage.setItem(RENEWAL_KEY, JSON.stringify(renewal));
  } catch {
    /* ignore */
  }
  return state;
}

export function readTopLevelRenewal(
  state?: string | null,
): TopLevelRenewal | null {
  if (!state) return null;
  try {
    const renewal = JSON.parse(sessionStorage.getItem(RENEWAL_KEY) || 'null');
    return renewal?.state === state && renewal.redirectUri ? renewal : null;
  } catch {
    return null;
  }
}

export function readRenewalRedirectUri(state?: string | null): string | null {
  const renewal = readTopLevelRenewal(state);
  return renewal ? String(renewal.redirectUri) : null;
}

export function clearRenewalRedirectUri(): void {
  try {
    sessionStorage.removeItem(RENEWAL_KEY);
  } catch {
    /* ignore */
  }
}

export function readPostAuthReturn(): string {
  try {
    return sessionStorage.getItem(POST_AUTH_RETURN_KEY) || '';
  } catch {
    return '';
  }
}

export function takePostAuthReturn(): string {
  const path = readPostAuthReturn();
  try {
    sessionStorage.removeItem(POST_AUTH_RETURN_KEY);
  } catch {
    /* ignore */
  }
  return path;
}

function randomState(): string {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join(
    '',
  );
}

function requestCodeInFrame(): Promise<string> {
  return new Promise((resolve, reject) => {
    const redirectUri = getKeycloakRedirectUri();
    let redirectOrigin = '';
    try {
      redirectOrigin = new URL(redirectUri, window.location.href).origin;
    } catch {
      redirectOrigin = '';
    }
    if (redirectOrigin !== window.location.origin) {
      reject(refused('Silent sign-in returns to another origin'));
      return;
    }
    const state = randomState();
    const frame = document.createElement('iframe');
    let timer: number | undefined;
    const finish = (error: Error | null, code?: string) => {
      window.clearTimeout(timer);
      frame.remove();
      if (error || !code) {
        reject(error || refused('Silent sign-in returned no code'));
        return;
      }
      resolve(code);
    };
    frame.setAttribute('sandbox', 'allow-same-origin');
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    frame.style.cssText =
      'position:absolute;width:0;height:0;border:0;visibility:hidden';
    frame.addEventListener('load', () => {
      let hash = '';
      try {
        hash = frame.contentWindow?.location.hash || '';
      } catch {
        finish(new Error('Silent sign-in ended on another origin'));
        return;
      }
      const params = new URLSearchParams(hash.replace(/^#/, ''));
      if (params.get('state') !== state) return;
      const error = params.get('error');
      if (error) framesRefused = true;
      finish(
        error ? refused(`Silent sign-in failed: ${error}`) : null,
        params.get('code') || undefined,
      );
    });
    timer = window.setTimeout(
      () => finish(new Error('Silent sign-in timed out')),
      FRAME_TIMEOUT_MS,
    );
    frame.src = getKeycloakSilentAuthUrl(state);
    document.body.appendChild(frame);
  });
}

async function exchangeCode(code: string): Promise<RenewedTokens> {
  let data: any;
  try {
    ({ data } = await axios.post(
      `${ENVIRONMENTS.API_BASE_URL_V2}/auth/login`,
      {
        code,
        redirect_uri: getKeycloakRedirectUri(),
        platformType: 'web',
        refCode: '',
      },
      { timeout: EXCHANGE_TIMEOUT_MS },
    ));
  } catch (error: any) {
    const message = `Session renewal exchange failed: ${errorMessage(error)}`;
    const status = error?.response?.status;
    throw status && status < 500 ? refused(message) : new Error(message);
  }
  if (data?.status === 'error') {
    throw refused(data?.message || 'Session renewal was refused');
  }
  if (!data?.token) throw new Error('Session renewal returned no token');
  return { token: data.token, idToken: data.id_token };
}

async function renew(): Promise<string> {
  if (!store.getState().common.auth?.token) {
    throw refused('Not signed in');
  }
  if (!isConfiguredKeycloakUrl(ENVIRONMENTS.KEYCLOAK_ACCOUNT)) {
    throw refused('Keycloak is not configured');
  }
  if (blocksThirdPartyCookies()) {
    throw refused('This browser blocks the Keycloak cookie in frames');
  }
  const code = await requestCodeInFrame();
  const receivedAt = Date.now();
  const renewed = await exchangeCode(code);
  const current = store.getState().common.auth?.token;
  if (!current) throw refused('Signed out during session renewal');
  const claims = readTokenClaims(renewed.token);
  if (!claims?.sub || claims.sub !== readTokenClaims(current)?.sub) {
    throw refused('Session renewal returned a different account');
  }
  learnClockSkew(renewed.token, receivedAt);
  store.dispatch(renewAuthTokens(renewed));
  return renewed.token;
}

export function renewSessionInPage(): Promise<string> {
  if (!renewal) {
    renewal = renew()
      .then(token => {
        retryAfter = 0;
        return token;
      })
      .catch(error => {
        retryAfter =
          Date.now() +
          (error?.refused ? REFUSED_COOLDOWN_MS : TRANSIENT_COOLDOWN_MS);
        console.warn('In-page session renewal failed:', errorMessage(error));
        throw error;
      })
      .finally(() => {
        renewal = null;
      });
  }
  return renewal;
}

export function renewSessionUnlessCoolingDown(): Promise<string> {
  if (!renewal && Date.now() < retryAfter) {
    return Promise.reject(new Error('Session renewal failed moments ago'));
  }
  return renewSessionInPage();
}

export async function getFreshToken(): Promise<string | undefined> {
  const token = store.getState().common.auth?.token;
  const coolingDown = !renewal && Date.now() < retryAfter;
  if (!token || coolingDown || !tokenNeedsRenewal(token)) return token;
  const renewing = renewSessionInPage().catch(() => undefined);
  if (!tokenIsExpiring(token)) return token;
  await renewing;
  return store.getState().common.auth?.token;
}

export function hasOpenDialog(): boolean {
  return Array.from(document.querySelectorAll('[role="dialog"]')).some(
    element => element.getClientRects().length > 0,
  );
}

export function whenNoDialogIsOpen(
  callback: () => void,
  maxWaitMs: number,
): void {
  if (!hasOpenDialog()) {
    callback();
    return;
  }
  if (dialogWait !== undefined) return;
  let heldMs = 0;
  let lastTick = Date.now();
  let wasVisible = !document.hidden;
  dialogWait = window.setInterval(() => {
    const now = Date.now();
    const visible = !document.hidden;
    if (visible && wasVisible) heldMs += now - lastTick;
    lastTick = now;
    wasVisible = visible;
    if (hasOpenDialog() && heldMs < maxWaitMs) return;
    window.clearInterval(dialogWait);
    dialogWait = undefined;
    callback();
  }, DIALOG_POLL_MS);
}
